import { Head, Link } from "@inertiajs/react";
import { ClipboardList, Plus, Sparkles } from "lucide-react";
import SchoolLayout from "@/components/schools/SchoolLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import RewardManager from "@/components/schools/rewards/RewardManager";
import SuggestedRewards from "@/components/schools/rewards/SuggestedRewards";
import OtherWaysToReward from "@/components/schools/rewards/OtherWaysToReward";
import { suggestStudentPrizes } from "@/lib/campaignPlanning";
import { pluralize } from "@/lib/format";

/**
 * Student prizes: prizes each student can win for their own reading (or
 * fundraising) — everyone who reaches an amount, a prize draw, or the top
 * of each class. Teachers hand them out from their classroom page; the
 * prize report rolls it up for the school.
 */
export default function StudentMilestones({ school, membership, campaign, milestones, create_path, disbursements_path, suggestions_path }) {
  const suggestions = suggestStudentPrizes({ start: campaign.start_on, end: campaign.end_on });

  return (
    <SchoolLayout
      school={school}
      membership={membership}
      eyebrow={campaign.name}
      title="Student prizes"
      actions={disbursements_path && (
        <Button variant="outline" size="sm" asChild>
          <Link href={disbursements_path}>
            <ClipboardList className="h-4 w-4" />
            Prize report
          </Link>
        </Button>
      )}
    >
      <Head title={`Student prizes · ${campaign.name}`} />

      <div>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Prizes each student earns
            </CardTitle>
            <CardDescription>
              Each student wins these on their own: by reaching an amount, in a prize draw, or by reading or raising the most in their class, division or school. Teachers mark them handed out from their classroom page, and the prize report shows who's still waiting.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RewardManager
              rewards={milestones}
              scope="student"
              createPath={create_path}
              locked={campaign.locked}
              addLabel="Add a student prize"
              removeWarning={removeWarning}
              emptyState={({ onAdd }) => (
                <div className="space-y-4">
                  {suggestions_path && suggestions.length > 0 && (
                    <SuggestedRewards
                      title="Three reading tiers for your campaign's length"
                      description="One almost everyone can reach, one that's a stretch, and one that's a real achievement. Tiers stack: a student who reaches the top one earns all three."
                      suggestions={suggestions}
                      appliesTo="student"
                      path={suggestions_path}
                      returnTo="student_milestones"
                      noun="prize"
                      secondaryAction={<Button variant="ghost" onClick={onAdd}>I'll add my own</Button>}
                    />
                  )}
                  {!(suggestions_path && suggestions.length > 0) && (
                    <>
                      <p className="pt-2 text-center text-sm text-muted-foreground">
                        No student prizes yet. Try one almost everyone can reach, one that's a stretch, and one that's a real achievement.
                      </p>
                      <div className="text-center">
                        <Button onClick={onAdd}>
                          <Plus className="h-4 w-4" />
                          Add your first prize
                        </Button>
                      </div>
                    </>
                  )}
                  <OtherWaysToReward onAdd={onAdd} />
                </div>
              )}
            />
          </CardContent>
        </Card>
      </div>
    </SchoolLayout>
  );
}

// Deleting a prize deletes every record of students earning it and any
// draw winners — say so, with the numbers.
function removeWarning(prize) {
  if (!prize.earned_count) return "No students have earned it yet, so nothing else is affected.";

  const parts = [`${pluralize(prize.earned_count, "student")} ${prize.earned_count === 1 ? "has" : "have"} earned it`];
  if (prize.handed_out_count) parts.push(`${prize.handed_out_count} already handed out`);
  if (prize.draws_count) parts.push(`${pluralize(prize.draws_count, "winner")} ${prize.draws_count === 1 ? "was" : "were"} drawn`);
  return `${parts.join(", ")}. Removing it deletes those records too, and can't be undone.`;
}
